import { User } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createUserGroupFilter from "@/utilities/createUserGroupFilter";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

const RESULTS_LIMIT = 50_000;

/** Ids Geotab uses on a trip that has no driver. Neither names a person. */
const NOT_A_DRIVER = new Set(["UnknownDriverId", "NoDriverId"]);

/**
 * Every driver in the database, subject to the group filter.
 *
 * Entities that reference a driver — `Trip`, `FuelTransaction` — come back carrying only its id,
 * so a name has to be looked up separately. One request for all of them rather than
 * `useUsersByIds`, which issues one `Get` per id: a report over a fleet references hundreds of
 * drivers and learns about them gradually, which would keep changing the id list and re-issue
 * every one of those requests. This key does not depend on which drivers have turned up.
 *
 * Archived drivers are included. A trip from three weeks ago belongs to whoever drove it, whether
 * or not they still work here.
 */
export function useDrivers() {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();

  return useQuery({
    queryKey: [
      "drivers",
      activeSessionId,
      groups?.map(group => group.id),
      groupFilterCondition,
    ] as const,

    queryFn: ({ signal }) =>
      geotabClient.get(
        "User",
        createUserGroupFilter(groups, groupFilterCondition, { isDriver: true }),
        RESULTS_LIMIT,
        signal,
      ),
  });
}

/**
 * Driver display names by id, for resolving the driver references on trips and fuel transactions.
 *
 * @returns A lookup from driver id to display name.
 */
export function useDriverNames(): Map<string, string> {
  const { data } = useDrivers();

  return useMemo(() => {
    const names = new Map<string, string>();
    for (const driver of data ?? []) {
      const name = getUserDisplayName(driver as User);
      if (name) {
        names.set(driver.id, name);
      }
    }
    return names;
  }, [data]);
}

/**
 * The id of the driver a referencing entity names, or `null` when it names none.
 *
 * @param reference - The driver as it came off the entity, populated with an id at most.
 */
export function driverIdOf(reference: { id?: string } | undefined | null): string | null {
  const id = reference?.id;
  return id && !NOT_A_DRIVER.has(id) ? id : null;
}
